import { Box, InputAdornment, Stack, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import { Search } from "@mui/icons-material";
import { useRef, useState } from "react";
import { Student, STUDENT_STATUS } from "../../types/students";
import { User } from "../../types/users";
import { useUpdateStudentMutation } from "../../services/students.service";
import KanbanColumn from "./KanbanColumn";
import StudentForm from "./StudentsForm";

interface Props {
  students: Student[];
  users: User[];
}

const ALL_STATUSES = Object.values(STUDENT_STATUS);

// Logical phase groupings so user can filter to a phase quickly
const PHASES: Record<string, string[]> = {
  All: ALL_STATUSES,
  Intake: ["initial"],
  Visa: ["visa", "visa:reminder", "visa:preparing", "visa:submitted"],
  Admission: ["admission:reminder", "admission:preparing", "admission:submitted", "issue resolution"],
  Registration: ["registration", "registration:reminder"],
  Deferment: ["deferment:preparing", "deferment:submitted"],
  Closing: ["pickup:reminder", "commission:pending", "completed successfully", "to be archived", "cooperation done"],
};

function KanbanBoard({ students, users }: Props) {
  const [updateStudent] = useUpdateStudentMutation();
  const draggingId = useRef<string | null>(null);
  const [search, setSearch] = useState("");
  const [phase, setPhase] = useState("All");
  const [editStudent, setEditStudent] = useState<Student | null>(null);

  const filteredStudents = students.filter((s) => {
    const q = search.toLowerCase();
    const matchesSearch =
      !q ||
      s.fullName?.toLowerCase().includes(q) ||
      s.refNo?.toLowerCase().includes(q) ||
      s.nationality?.toLowerCase().includes(q);
    return matchesSearch;
  });

  const visibleStatuses = PHASES[phase] ?? ALL_STATUSES;

  const grouped = visibleStatuses.reduce<Record<string, Student[]>>((acc, status) => {
    acc[status] = filteredStudents.filter((s) => s.status === status);
    return acc;
  }, {});

  const totalStudents = filteredStudents.length;
  const totalCommission = filteredStudents.reduce(
    (sum, s) => sum + (Number((s as any).commission) || 0),
    0
  );

  const handleDrop = async (newStatus: string) => {
    if (!draggingId.current) return;
    const student = students.find((s) => s._id === draggingId.current);
    draggingId.current = null;
    if (!student || student.status === newStatus) return;
    await updateStudent({ ...student, status: newStatus } as Student);
  };

  const handleEditSubmit = (student: Student) => {
    // Strip empty / null fields the same way the table does
    const cleaned = { ...student } as any;
    for (const key in cleaned) {
      if (cleaned[key] === "" || cleaned[key] === null) delete cleaned[key];
      if (cleaned[key] === "N/A") cleaned[key] = null;
    }
    updateStudent(cleaned as Student).then(() => setEditStudent(null));
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 1.5 }}>
      {/* Toolbar */}
      <Stack direction="row" alignItems="center" spacing={2} sx={{ flexShrink: 0 }}>
        <TextField
          size="small"
          placeholder="Search students..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 220 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" sx={{ color: "#94A3B8" }} />
              </InputAdornment>
            ),
          }}
        />

        <ToggleButtonGroup
          size="small"
          value={phase}
          exclusive
          onChange={(_, val) => val && setPhase(val)}
          sx={{ height: 36 }}
        >
          {Object.keys(PHASES).map((p) => (
            <ToggleButton
              key={p}
              value={p}
              sx={{ px: 1.5, fontSize: "0.72rem", fontWeight: 600, textTransform: "none" }}
            >
              {p}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Stack direction="row" spacing={2} sx={{ ml: "auto !important" }}>
          <Typography variant="body2" color="text.secondary">
            <b>{totalStudents}</b> students
          </Typography>
          {totalCommission > 0 && (
            <Typography variant="body2" color="text.secondary">
              <b style={{ color: "#059669" }}>RM {totalCommission.toLocaleString()}</b> commission
            </Typography>
          )}
        </Stack>
      </Stack>

      {/* Board */}
      <Box
        sx={{
          flex: 1,
          overflowX: "auto",
          overflowY: "hidden",
          "&::-webkit-scrollbar": { height: 6 },
          "&::-webkit-scrollbar-track": { bgcolor: "#F1F5F9", borderRadius: 999 },
          "&::-webkit-scrollbar-thumb": { bgcolor: "#CBD5E1", borderRadius: 999 },
        }}
      >
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ minWidth: "max-content", height: "100%", px: 0.5, py: 0.25 }}
        >
          {visibleStatuses.map((status) => (
            <KanbanColumn
              key={status}
              status={status}
              students={grouped[status] ?? []}
              users={users}
              onDragStart={(id) => { draggingId.current = id; }}
              onDrop={handleDrop}
              onCardClick={setEditStudent}
            />
          ))}
        </Stack>
      </Box>

      {editStudent && (
        <StudentForm
          open={!!editStudent}
          edit
          selectedStudent={editStudent}
          users={users}
          handleClose={() => setEditStudent(null)}
          submitHanlder={handleEditSubmit}
        />
      )}
    </Box>
  );
}

export default KanbanBoard;
